<template>
  <Checkout
    v-bind="$attrs"
    :action="route('checkout.shipping', order)"
    :back="route('checkout', { order, step: 'contact' })"
    :order="order"
    :submit="submit"
    action-text="Weiter zur Zahlung"
    back-text="Zurück zu den Informationen"
    step="shipping"
  >
    <template #default>
      <h1 class="text-lg text-blue-500 mb-2">Versand</h1>

      <div class="border border-gray-300 rounded-md divide-y divide-gray-300 text-gray-800">
        <label class="flex items-center px-4 py-3.5 cursor-pointer leading-tight">
          <input v-model="form.shipping_method" class="form-radio flex-shrink-0 mr-4" type="radio" name="shipping_method" value="email">
          <span class="flex-1">E-Mail</span>
          <span>–</span>
        </label>
        <label class="flex items-center px-4 py-3.5 cursor-pointer leading-tight">
          <input v-model="form.shipping_method" class="form-radio flex-shrink-0 mr-4" type="radio" name="shipping_method" value="mail">
          <span class="flex-1">Postversand</span>
          <strong class="font-semibold">{{ formatPrice(shipping.price) }}</strong>
        </label>
      </div>

      <template v-if="form.shipping_method === 'mail'">
        <div class="mt-12">
          <h2 class="text-lg text-blue-500 mb-1">Lieferadresse</h2>
          <p class="text-gray-700 text-sm leading-normal">
            Bitte geben Sie die Adresse an, an die wir Ihre Bestellung versenden sollen.
            Wählen Sie im Anschluss aus, ob die Rechnung an die Lieferanschrift oder an eine abweichende Adresse verschickt werden soll.
          </p>
        </div>

        <div class="border border-gray-300 rounded-md divide-y divide-gray-300 mt-6 text-gray-800 overflow-hidden">
          <label class="flex items-center px-4 py-3.5 cursor-pointer leading-tight">
            <input v-model="form.shipping_address" class="form-radio flex-shrink-0 mr-4" type="radio" name="shipping_address" :value="false">
            <span>Mit der Rechnungsadresse identisch</span>
          </label>
          <div>
            <label class="flex items-center px-4 py-3.5 cursor-pointer leading-tight">
              <input v-model="form.shipping_address" class="form-radio flex-shrink-0 mr-4" type="radio" name="shipping_address" :value="true">
              <span>Eine andere Lieferadresse verwenden</span>
            </label>
            <div v-if="form.shipping_address" class="bg-gray-100 grid xl:grid-cols-12 gap-4 px-4 py-8 border-t border-gray-300">
              <Fields
                v-model="form"
                :fields="fields"
                :errors="form.errors"
              />
            </div>
          </div>
        </div>
      </template>

      <template v-else>
        <div class="mt-12 bg-blue-muted px-4 py-3 rounded">
          <h2 class="text-lg text-blue-500 mb-1">Hinweis E-Mail-Ausdruck</h2>
          <p class="text-gray-700 text-sm leading-normal">
            Tickets werden an die von Ihnen angegebene E-Mail-Adresse als PDF-Datei geschickt – zum Selbstausdruck oder Vorzeigen auf Ihrem mobilen Endgerät.
            Bei Gutscheinen und Abos erhalten Sie per E-Mail einen Code, den Sie bei Bestellungen einlösen können.
            Für haptische Abokarten oder Gutscheine wählen Sie bitte
            <button class="cursor-pointer underline" @click="form.shipping_method = 'mail'">Postversand</button>.
          </p>
        </div>
      </template>

    </template>
  </Checkout>
</template>

<script>
import Checkout from 'Web/Layouts/Checkout.vue';
import Fields from 'Web/Form/Fields.vue';
import { onMounted, reactive, toRefs } from 'vue';
import { useForm } from '@inertiajs/inertia-vue3';
import mapValues from 'lodash/mapValues';
import formatPrice from '@/helpers/formatPrice';

export default {
  inheritAttrs: false,
  components: {
    Checkout,
    Fields,
  },
  props: {
    order: {
      type: Object,
      required: true,
    },
    shipping: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const state = reactive({
      fields: null,
      form: null,
    });

    state.fields = {
      first_name: {
        width: '1/2',
        label: 'Vorname',
        type: 'text',
        autocomplete: 'given-name',
      },
      last_name: {
        width: '1/2',
        label: 'Nachname',
        type: 'text',
        autocomplete: 'family-name',
      },
      company: {
        label: 'Firma (optional)',
        type: 'text',
        autocomplete: 'organization',
      },
      street: {
        label: 'Straße und Hausnummer',
        type: 'text',
        autocomplete: 'street-address',
      },
      postal_code: {
        width: '1/3',
        label: 'Postleitzahl',
        type: 'text',
        autocomplete: 'postal-code',
      },
      city: {
        width: '2/3',
        label: 'Ort',
        type: 'text',
        autocomplete: 'address-level2',
      },
      country: {
        label: 'Land',
        type: 'select',
        autocomplete: 'country',
        options: {
          BE: 'Belgien',
          DE: 'Deutschland',
          NL: 'Niederlande',
        }
      },
      phone: {
        label: 'Telefon (optional)',
        type: 'email',
        autocomplete: 'tel',
      },
    }

    const data = {
      ...mapValues(state.fields, (value, key) => null),
      shipping_method: null,
      shipping_address: null,
    }

    Object.assign(data, props.order.shipping_address, {
      shipping_method: props.order.shipping_method ?? 'email',
      shipping_address: props.order?.shipping_address != null,
    });

    state.form = useForm('shipping', data);

    onMounted(() => {
      state.form.clearErrors();
    });

    function submit(event) {
      state.form.post(event.target.action);
    }


    return {
      ...toRefs(state),
      formatPrice,
      submit,
    }
  }
}
</script>
